/**
 * Auth domain models — mirror the backend `AuthedUser`, `LoginResponse`,
 * and `MfaRequiredResponse` shapes from `ebt-backend/src/modules/auth/types.ts`.
 *
 * Kept in lockstep with the backend by hand because the codegen surface
 * does not yet expose response DTOs as named `components.schemas`
 * entries (backend follow-up — annotate auth responses with @ApiResponse
 * `{ type }` so the OpenAPI spec carries them).
 */

export type AuthAudience = 'internal' | 'customer';

export interface AuthedUser {
  id: string;
  email: string;
  roleKey: string;
  permissions: string[];
  audience: AuthAudience;
  tenantId: string | null;
  /** Only set when audience='customer'. */
  customerId?: string;
}

export interface LoginResponse {
  accessToken: string;
  /** Seconds until the access token expires. */
  expiresIn: number;
  user: AuthedUser;
}

export interface MfaRequiredResponse {
  mfaRequired: true;
  /** Short-lived (~5min) signed token proving the password step passed. */
  mfaToken: string;
  expiresIn: number;
}

export type LoginOrMfa = LoginResponse | MfaRequiredResponse;

export function isMfaRequired(r: LoginOrMfa): r is MfaRequiredResponse {
  return (r as MfaRequiredResponse).mfaRequired === true;
}

/** Tristate auth status emitted by `AuthStore`. */
export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';
